import React, { useRef, useEffect, useMemo } from "react";
import * as Chart from "chart.js";
import moment from "moment-hijri";
import HeadingComponent from "../shared/heading";

Chart.Chart.register(...Chart.registerables);

// ─── COMPONENT ───────────────────────────────────────────────────────────────
const NewStackedChart = ({
  title = "",
  subtitle = "",
  chartTitle = "",
  yAxisLabel = "Revenue by Payment Source",
  bottomLabel = "Months",
  pndData = [],
  appWebData = [],
  appToTotalData = [],
  months = [],
  yearRanges = [],
  colors: colorsProp = {},
  appWebLabel = "APP/WEB_Amount_Collected",
  pndLabel = "PND_Amount_Collected",
  ratioLabel = "App to Total Revenue",
  showDataLabels = true,
  showRamadanRow = true,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  // ✅ Store value label rects for Ramadan avoidance
  const finalLabelRects = useRef([]);
  const containerRef = useRef(null);
  const tableRef = useRef(null);
  const labelColRef = useRef(null);
  

  // ── Merge defaults ──────────────────────────────────────────────────────
  const colors = {
    appWeb: colorsProp?.transaction ?? "rgba(230,140,40,1)",
    pnd:    colorsProp?.revenue     ?? "rgba(80,140,220,1)",
    ratio:  colorsProp?.als         ?? "#FFD700",
  };

  // ── Dynamic Y axis bounds (LEFT) ──
const yBounds = useMemo(() => {
  const totals = months.map((_, i) => {
    const p = Number(pndData[i] ?? 0);
    const a = Number(appWebData[i] ?? 0);
    return p + a;
  });

  const maxVal = Math.max(...totals, 0);

  return {
    min: 0,
    max: Math.ceil(maxVal * 1.15),
  };
}, [pndData, appWebData, months]);


  
  // ── Dynamic Y2 axis bounds ──
  const y2Bounds = useMemo(() => {
    const validVals = appToTotalData.filter((v) => v != null && !isNaN(v));
    if (!validVals.length) return { min: 0, max: 100 };
    const maxVal = Math.max(...validVals);
    const roundedMax = Math.ceil((maxVal * 1.25) / 5) * 5;
    return { min: 0, max: Math.min(100, roundedMax) };
  }, [appToTotalData]);

  // ── Ramadan detection — returns array of month indices ──
  const getRamadanMonths = useMemo(() => {
    if (!showRamadanRow || !months?.length || !yearRanges?.length) return [];
    const ramadanIndices = [];
    let idx = 0;
    const monthMap = { Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11 };
    yearRanges.forEach((yr) => {
      const year = parseInt(yr.year);
      for (let i = 0; i < yr.span; i++) {
        const mon = months[idx];
        const mNum = monthMap[mon] ?? (idx % 12);
        const hijriYear = year - 579;
        const ramadanFirstDay = moment(`${hijriYear}-9-1`, 'iYYYY-iM-iD');
        const ramadanStartGregMonth = ramadanFirstDay.month();
        const ramadanStartGregYear  = ramadanFirstDay.year();

        if (
          ramadanStartGregYear === year &&
          ramadanStartGregMonth === mNum
        ) {
          ramadanIndices.push(idx);
        }
        idx++;
      }
    });
    return ramadanIndices;
  }, [months, yearRanges, showRamadanRow]);

  useEffect(() => {
    if (!chartRef.current) return;
    const ctx = chartRef.current.getContext("2d");
    if (chartInstanceRef.current) chartInstanceRef.current.destroy();

    // ✅ Clear finalLabelRects
    finalLabelRects.current = [];

    // ✅ Bar/line value labels get computed first, but NOT drawn immediately.
    // ramadanPlugin gets first pick of its natural spot, nudges any clashing
    // value label out of the way, and only then does drawValueLabelsPlugin
    // draw everything — followed by drawRamadanBoxesPlugin drawing Ramadan
    // LAST so it always sits on top, fully legible.
    let pendingBarLabels = [];
    let pendingLineLabels = [];
    // ✅ Ramadan box specs get computed/nudged first, then drawn LAST (see
    // drawRamadanBoxesPlugin) so they always render on top of everything.
    let pendingRamadanBoxes = [];

    // ── Ramadan month indices set ──
    const ramadanIndices = new Set(getRamadanMonths);

    // ── custom legend icons ──
    const customLegendPlugin = {
      id: "customLegend",
      afterDraw(chart) {
        const legend = chart.legend;
        if (!legend?.legendItems) return;
        const ctx = chart.ctx;
        ctx.save();
        legend.legendItems.forEach((item, i) => {
          const hitBox = legend.legendHitBoxes?.[i];
          if (!hitBox) return;
          const centerY = hitBox.top + hitBox.height / 2;
          const iconW = 22;
          const iconX = hitBox.left;
          ctx.clearRect(iconX, hitBox.top, iconW + 2, hitBox.height);

          if (i === 0) {
            ctx.strokeStyle = colors.pnd;
            ctx.lineWidth = 1.5;
            ctx.strokeRect(iconX, centerY - 5, iconW, 10);
          } else if (i === 1) {
            ctx.strokeStyle = colors.appWeb;
            ctx.lineWidth = 1.5;
            ctx.strokeRect(iconX, centerY - 5, iconW, 10);
          } else if (i === 2) {
            ctx.strokeStyle = colors.ratio;
            ctx.lineWidth = 2;
            ctx.setLineDash([6, 3]);
            ctx.beginPath();
            ctx.moveTo(iconX, centerY);
            ctx.lineTo(iconX + iconW, centerY);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        });
        ctx.restore();
      },
    };

    // ── shadow ──
    const shadowPlugin = {
      id: "shadowPlugin",
      beforeDatasetDraw(chart, args) {
        const { ctx } = chart;
        ctx.save();
        const shadowMap = [
          "rgba(80,140,220,0.75)",
          "rgba(230,140,40,0.75)",
          colors.ratio + "88",
        ];
        ctx.shadowColor   = shadowMap[args.index] ?? "transparent";
        ctx.shadowBlur    = 10;
        ctx.shadowOffsetY = 3;
      },
      afterDatasetDraw(chart) {
        const { ctx } = chart;
        ctx.shadowColor   = "transparent";
        ctx.shadowBlur    = 0;
        ctx.shadowOffsetY = 0;
        ctx.restore();
      },
    };

    // ── bar value labels (compute only — drawValueLabelsPlugin draws them) ──
    const revenueLabelPositions = [];
    const barLabelPlugin = {
      id: "barLabels",
      afterDatasetsDraw(chart) {
        if (!showDataLabels) return;
        revenueLabelPositions.length = 0;
        finalLabelRects.current.length = 0;
        pendingBarLabels.length = 0;

        chart.data.datasets.forEach((dataset, i) => {
          if (dataset.type !== "bar") return;
          const meta = chart.getDatasetMeta(i);
          const isPnd = dataset.label === pndLabel;

          meta.data.forEach((bar, index) => {
            // Skip Ramadan months
            if (ramadanIndices.has(index) && dataset.label === appWebLabel) return;

            const value = dataset.data[index];
            if (value == null || value === 0) return;
            const barH = Math.abs(bar.height);

const formatted = value.toLocaleString();
const cx = bar.x;
let cy = barH < 14 ? bar.y - 8 : bar.y + barH / 2;
            ctx.font = "bold 10px Arial";
            const tw = ctx.measureText(formatted).width;
            const bw = tw + 10, bh = 16;

            let attempts = 0, overlapping = true;
            while (overlapping && attempts < 10) {
              overlapping = false;
              for (const p of revenueLabelPositions) {
                if (Math.abs(cx - p.x) < (bw / 2 + p.w / 2 + 2) && Math.abs(cy - p.y) < (bh / 2 + p.h / 2 + 2)) {
                  cy = p.y - p.h / 2 - bh / 2 - 3;
                  overlapping = true;
                  break;
                }
              }
              attempts++;
            }
            revenueLabelPositions.push({ x: cx, y: cy, w: bw, h: bh });

            // Add to finalLabelRects (kept for reference, no longer used to
            // block Ramadan placement — see ramadanPlugin)
            finalLabelRects.current.push({
              left: cx - bw / 2 - 6,
              right: cx + bw / 2 + 6,
              top: cy - bh / 2 - 6,
              bottom: cy + bh / 2 + 6,
            });

            // ✅ Don't draw yet — store spec, drawValueLabelsPlugin draws it
            // after ramadanPlugin has nudged anything that clashes.
            pendingBarLabels.push({
              x: cx, y: cy, w: bw, h: bh,
              text: formatted,
              borderColor: dataset.borderColor,
              shadowColor: isPnd ? "rgba(80,140,220,0.75)" : "rgba(230,140,40,0.75)",
            });
          });
        });
      },
    };

    // ── line labels (compute only — drawValueLabelsPlugin draws them) ──
    const lineLabelPlugin = {
      id: "lineLabels",
      afterDatasetsDraw(chart) {
        if (!showDataLabels) return;
        pendingLineLabels.length = 0;
        const ctx2 = chart.ctx;
        const lblArr = [];

        chart.data.datasets.forEach((dataset, dsIndex) => {
          if (dataset.type !== "line") return;
          const meta = chart.getDatasetMeta(dsIndex);
          if (!meta?.data) return;

          meta.data.forEach((point, pointIndex) => {
            // if (ramadanIndices.has(pointIndex)) return;
            if (!point || point.x == null || point.y == null) return;
            const value = dataset.data[pointIndex];
            if (value == null || value === 0 || value === undefined) return;

            const numValue = typeof value === "number" && !isNaN(value) ? value : 0;
            const label = numValue.toFixed(0) + "%";
            ctx2.font = "bold 9px Arial";
            const tw = ctx2.measureText(label).width;

            lblArr.push({
              x: point.x,
              y: point.y - 12,
              text: label,
              color: colors.ratio,
              width: tw + 10,
              height: 16,
              datasetIndex: dsIndex,
              pointIndex,
              originalY: point.y,
            });
          });
        });

        lblArr.sort((a, b) => a.x - b.x);

        const vPad = 4;
        const overlap = (r1, r2) =>
          !(r1.right + 5 < r2.left || r1.left - 5 > r2.right || r1.bottom + vPad < r2.top || r1.top - vPad > r2.bottom);

        for (let i = 0; i < lblArr.length; i++) {
          const curr = lblArr[i];
          let adjusted = false;
          let iter = 0;
          do {
            adjusted = false;
            const cr = {
              left: curr.x - curr.width / 2,
              right: curr.x + curr.width / 2,
              top: curr.y - curr.height / 2,
              bottom: curr.y + curr.height / 2
            };

            // Check with bar labels
            for (const rv of revenueLabelPositions) {
              const rvTop = rv.y - rv.h / 2;
              const rvBottom = rv.y + rv.h / 2;
              const rvLeft = rv.x - rv.w / 2;
              const rvRight = rv.x + rv.w / 2;
              const rvRect = { left: rvLeft, right: rvRight, top: rvTop, bottom: rvBottom };
              if (overlap(cr, rvRect)) {
                curr.y = rvTop - curr.height / 2 - vPad;
                adjusted = true;
                break;
              }
            }

            // Check with other line labels
            if (!adjusted) {
              for (let j = 0; j < i; j++) {
                const ot = lblArr[j];
                const or = {
                  left: ot.x - ot.width / 2,
                  right: ot.x + ot.width / 2,
                  top: ot.y - ot.height / 2,
                  bottom: ot.y + ot.height / 2
                };
                if (overlap(cr, or)) {
                  curr.y = ot.y - (ot.height + vPad);
                  adjusted = true;
                  break;
                }
              }
            }
            iter++;
            if (iter >= 50) break;
          } while (adjusted);
        }

        // ✅ Store finalized specs — drawn later by drawValueLabelsPlugin,
        // AFTER ramadanPlugin has had a chance to nudge any clashing ones.
        lblArr.forEach((lbl) => pendingLineLabels.push(lbl));
      },
    };

    // ── line shadow ──
   const lineShadowPlugin = {
  id: "lineShadowPlugin",
  beforeDatasetsDraw(chart, args) {
    // Sirf line datasets ke liye shadow set karo
    if (chart.data.datasets[args.index]?.type !== "line") return;
    const { ctx } = chart;
    ctx.save();
    ctx.shadowColor = colors.ratio + "88";
    ctx.shadowBlur = 12;
    // ctx ki shadow state set ho gayi — Chart.js ab khud line draw karega
    // Hum yahan manually line NAHI khinchte
  },
  afterDatasetDraw(chart, args) {
    if (chart.data.datasets[args.index]?.type !== "line") return;
    const { ctx } = chart;
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.restore();
  },
};

    // ── RAMADAN PLUGIN — computes position + nudges clashing value labels ──
    const ramadanPlugin = showRamadanRow && getRamadanMonths.length > 0 ? {
  id: "ramadanPlugin",
  afterDatasetsDraw(chart) {
    const ctx = chart.ctx;
    const xAxis = chart.scales.x;
    const yAxis = chart.scales.y;

    pendingRamadanBoxes.length = 0;

    const ramadanMonthIndices = getRamadanMonths;
    if (ramadanMonthIndices.length === 0) return;

    const BOX_H   = 24;
    const BOX_W   = 165;
    const BR      = 5;
    const ARROW_W = 16;
    const H_PAD   = 5;
    const V_PAD   = 5;

    const chartTop    = yAxis.top;
    const chartBottom = yAxis.bottom;
    const chartLeft   = xAxis.left;
    const chartRight  = xAxis.right;

    // ✅ FIX: clampX ab anchorX (dataX) ke around centered rakhta hai
    const clampX = (x, anchorX) => {
      const maxShift = BOX_W * 0.4;
      const anchored = Math.max(
        anchorX - maxShift,
        Math.min(anchorX + maxShift - BOX_W, x)
      );
      return Math.max(
        chartLeft + 4,
        Math.min(chartRight - BOX_W - 4, anchored)
      );
    };

    const clampY = (y) =>
  Math.max(chartTop - 10, Math.min(chartBottom - BOX_H - 4, y));

    const mkRect = (l, t, w, h) => ({
      left: l, right: l + w, top: t, bottom: t + h,
    });

    const overlaps = (a, b, hPad = H_PAD, vPad = V_PAD) =>
      a.left   - hPad < b.right  + hPad &&
      a.right  + hPad > b.left   - hPad &&
      a.top    - vPad < b.bottom + vPad &&
      a.bottom + vPad > b.top    - vPad;

    // ── STEP 1: Obstacles ─────────────────────────────────────────────────
    // ✅ Value labels are NOT obstacles here anymore — Ramadan claims its
    // natural spot and any clashing value label gets nudged out of the way
    // afterwards (see the nudge step below), instead of Ramadan dodging them.
    const obstacles = [];

    chart.data.datasets.forEach((dataset, dsIdx) => {
      if (dataset.type !== "bar") return;
      const meta = chart.getDatasetMeta(dsIdx);
      if (meta.hidden) return;
      meta.data.forEach((bar) => {
        if (!bar) return;
        try {
          const props = bar.getProps(["x", "y", "base", "width"], true);
          const bL   = props.x - props.width / 2;
          const bR   = props.x + props.width / 2;
          const bTop = Math.min(props.y, props.base);
          const bBot = Math.max(props.y, props.base);
          if (!isNaN(bL) && !isNaN(bTop)) {
            obstacles.push({ left: bL - 3, right: bR + 3, top: bTop - 3, bottom: bBot + 3 });
          }
        } catch (_) {}
      });
    });

    chart.data.datasets.forEach((dataset, dsIdx) => {
      if (dataset.type === "bar") return;
      const meta = chart.getDatasetMeta(dsIdx);
      if (meta.hidden) return;
      meta.data.forEach((pt) => {
        if (!pt) return;
        const px = pt.x, py = pt.y;
        if (px == null || py == null || isNaN(px) || isNaN(py)) return;
        obstacles.push({ left: px - 4, right: px + 4, top: py - 4, bottom: py + 4 });
      });
    });

    // ── STEP 2: Helpers ───────────────────────────────────────────────────
    const getStackedBarTopY = (idx) => {
      const barDatasets = [];
      chart.data.datasets.forEach((dataset, i) => {
        if (dataset.type === "bar" && dataset.data[idx] != null && dataset.data[idx] > 0) {
          const meta = chart.getDatasetMeta(i);
          if (meta?.data?.[idx]) {
            barDatasets.push({ dataset, meta, bar: meta.data[idx] });
          }
        }
      });
      if (barDatasets.length === 0) return yAxis.getPixelForValue(0);
      barDatasets.sort((a, b) => (a.dataset.order || 0) - (b.dataset.order || 0));
      const topBar = barDatasets[barDatasets.length - 1];
      if (topBar?.bar) {
        try {
          const props = topBar.bar.getProps(["y", "base"], true);
          return Math.min(props.y, props.base ?? props.y);
        } catch (_) {
          return Math.min(topBar.bar.y, topBar.bar.base ?? topBar.bar.y);
        }
      }
      return yAxis.getPixelForValue(0);
    };

    const getTotalRevenueForIndex = (idx) => {
  const appWebDataset = chart.data.datasets.find(
    (ds) => ds.type === "bar" && ds.label === appWebLabel
  );
  if (!appWebDataset) return 0;
  return appWebDataset.data[idx] ?? 0;
};

    const getMonthName = (idx) => months[idx] || "";

    const getYearForIndex = (idx) => {
      let counter = 0;
      for (const yr of yearRanges) {
        if (idx >= counter && idx < counter + yr.span) return yr.year;
        counter += yr.span;
      }
      return "";
    };

    // ── STEP 3: Build items ───────────────────────────────────────────────
    const items = [];
    ramadanMonthIndices.forEach((idx) => {
      const val        = getTotalRevenueForIndex(idx);
      const year       = getYearForIndex(idx);
      const monthName  = getMonthName(idx);
      const formattedVal = (typeof val === "number" && !isNaN(val))
        ? val.toLocaleString() : "0";
      const dataY = getStackedBarTopY(idx);
      items.push({
        label:  `RAMADHAN ${year} ${monthName} : ${formattedVal}`,
        dataX:  xAxis.getPixelForValue(idx),
        dataY,
        revenue: val,
      });
    });

    if (items.length === 0) return;

    // ── STEP 4: Candidate grid ────────────────────────────────────────────
    // ── STEP 4: Candidate grid ─────────────────────────────────────────────────
// ── STEP 4: Candidate grid ─────────────────────────────────────────────
// ✅ ONLY vertical movement — dx hamesha 0
// Arrow kabhi teda nahi hoga kyunki box hamesha dataX pe centered hai
const buildCandidates = () => {
  const result = [];
  const vertGaps = [
    8, 20, 36, 55, 78, 105, 135, 170, 210, 255, 300, 350, 400, 450
  ];
  // ✅ Pehle pure vertical (dx=0), phir thoda horizontal sirf last resort
  const horizShifts = [0, 15, -15, 35, -35, 60, -60, 100, -100];
  for (const dy of vertGaps) {
    // dx=0 har dy ke liye pehle try karo
    result.push([0, dy]);
  }
  // Sirf agar sab vertical fail ho tab horizontal try karo
  for (const dy of vertGaps) {
    for (const dx of horizShifts) {
      if (dx !== 0) result.push([dx, dy]);
    }
  }
  return result;
};
    const candidateOffsets = buildCandidates();

    // ── STEP 5: Place labels ──────────────────────────────────────────────
    const placedBoxes = [];

    items.forEach(({ label, dataX, dataY }) => {
      const arrowTipY = dataY;
      let chosenX = null;
      let chosenY = null;
      let placed  = false;

      for (const [dx, dy] of candidateOffsets) {
        // ✅ clampX ab anchorX=dataX ke saath call hota hai
        const candidateBoxX = clampX(dataX + dx - BOX_W / 2, dataX);
        const candidateBoxY = clampY(arrowTipY - dy - BOX_H);
        const testRect  = mkRect(candidateBoxX, candidateBoxY, BOX_W, BOX_H);
        const allObs    = [...obstacles, ...placedBoxes];
        if (!allObs.some((obs) => overlaps(testRect, obs))) {
          chosenX = candidateBoxX;
          chosenY = candidateBoxY;
          placed  = true;
          break;
        }
      }

      if (!placed) {
        let bestScore = Infinity;
        let bestX = clampX(dataX - BOX_W / 2, dataX);
        let bestY = chartTop + 6;
        candidateOffsets.slice(0, 60).forEach(([dx, dy]) => {
          const bx = clampX(dataX + dx - BOX_W / 2, dataX);
          const by = clampY(arrowTipY - dy - BOX_H);
          const r  = mkRect(bx, by, BOX_W, BOX_H);
          const score = [...obstacles, ...placedBoxes]
            .filter((obs) => overlaps(r, obs, 0, 0)).length;
          if (score < bestScore) { bestScore = score; bestX = bx; bestY = by; }
        });
        chosenX = bestX;
        chosenY = bestY;
      }

      const chosenRect = mkRect(chosenX, chosenY, BOX_W, BOX_H);
      placedBoxes.push(chosenRect);
      obstacles.push({
        left:   chosenRect.left   - H_PAD,
        right:  chosenRect.right  + H_PAD,
        top:    chosenRect.top    - V_PAD,
        bottom: chosenRect.bottom + V_PAD,
      });

      // ── STEP 6: Nudge clashing value labels, then save spec for later draw ──
      const bTop = chosenY;
      const bBot = chosenY + BOX_H;

      // ✅ safeBaseCX: dataX prefer, sirf box edge pe clamp
      const safeBaseCX = Math.max(
        chosenX + ARROW_W / 2 + BR + 1,
        Math.min(dataX, chosenX + BOX_W - ARROW_W / 2 - BR - 1)
      );

      // ✅ Ramadan ne apni jagah le li — box aur uske arrow/tail dono ke
      // saath jo bhi value label clash kar rahi hai usay upar/neeche
      // (jis taraf usay zyada jagah mile) khiska do.
      {
        const ramadanBoxRect = { left: chosenX, right: chosenX + BOX_W, top: bTop, bottom: bBot };
        const ramadanTailRect = {
          left: Math.min(safeBaseCX, dataX) - ARROW_W / 2 - 2,
          right: Math.max(safeBaseCX, dataX) + ARROW_W / 2 + 2,
          top: bBot,
          bottom: arrowTipY,
        };
        const ramadanObstacles = [ramadanBoxRect, ramadanTailRect];
        const singleClash = (a, b) =>
          !(a.right < b.left || a.left > b.right || a.bottom < b.top || a.top > b.bottom);
        const clashesRamadan = (r) => ramadanObstacles.some((o) => singleClash(r, o));

        const minY = chartTop + 8;
        const maxY = chartBottom - 8;
        const pushStep = 6;

        const nudge = (lbl, getW, getH) => {
          const w = getW(lbl), h = getH(lbl);
          let boxY = lbl.y - h / 2;
          let rect = { left: lbl.x - w / 2 - 4, right: lbl.x + w / 2 + 4, top: boxY - 2, bottom: boxY + h + 4 };
          if (!clashesRamadan(rect)) return;

          const isAbove = lbl.y < arrowTipY;
          let guard = 0;
          while (clashesRamadan(rect) && guard < 80) {
            let nextY = lbl.y + (isAbove ? -pushStep : pushStep);
            nextY = Math.max(minY, Math.min(maxY, nextY));
            if (nextY === lbl.y) break;
            lbl.y = nextY;
            boxY = lbl.y - h / 2;
            rect = { left: lbl.x - w / 2 - 4, right: lbl.x + w / 2 + 4, top: boxY - 2, bottom: boxY + h + 4 };
            guard++;
          }

          if (clashesRamadan(rect)) {
            // Fallback: try the other direction from the original anchor
            const orig = lbl.y;
            let tryY = arrowTipY;
            let g2 = 0;
            let tryBoxY = tryY - h / 2;
            let tryRect = { left: lbl.x - w / 2 - 4, right: lbl.x + w / 2 + 4, top: tryBoxY - 2, bottom: tryBoxY + h + 4 };
            while (clashesRamadan(tryRect) && g2 < 80) {
              let nextY = tryY + (isAbove ? pushStep : -pushStep);
              nextY = Math.max(minY, Math.min(maxY, nextY));
              if (nextY === tryY) break;
              tryY = nextY;
              tryBoxY = tryY - h / 2;
              tryRect = { left: lbl.x - w / 2 - 4, right: lbl.x + w / 2 + 4, top: tryBoxY - 2, bottom: tryBoxY + h + 4 };
              g2++;
            }
            lbl.y = !clashesRamadan(tryRect) ? tryY : orig;
          }
        };

        pendingBarLabels.forEach((lbl) => nudge(lbl, (l) => l.w, (l) => l.h));
        pendingLineLabels.forEach((lbl) => nudge(lbl, (l) => l.width, (l) => l.height));
      }

      pendingRamadanBoxes.push({ chosenX, chosenY, bTop, bBot, BOX_W, BR, ARROW_W, dataX, arrowTipY, safeBaseCX, label });
    });
  },
} : { id: "ramadanPlugin_disabled", afterDraw() {} };

    // ✅ DRAW VALUE LABELS PLUGIN — draws bar labels, line labels, and their
    // connector lines, AFTER ramadanPlugin has nudged any clashing ones.
    const drawValueLabelsPlugin = {
      id: "drawValueLabels",
      afterDatasetsDraw(chart) {
        if (!showDataLabels) return;
        const ctx2 = chart.ctx;

        // Bar labels
        pendingBarLabels.forEach((lbl) => {
          const { x: cx, y: cy, w: bw, h: bh, text, borderColor, shadowColor } = lbl;
          ctx2.save();
          ctx2.font = "bold 10px Arial";
          ctx2.shadowColor = shadowColor;
          ctx2.shadowBlur = 6;
          ctx2.strokeStyle = borderColor;
          ctx2.lineWidth = 1;
          const roundRect = (x, y, w, h, r) => {
            ctx2.beginPath();
            ctx2.moveTo(x + r, y);
            ctx2.lineTo(x + w - r, y);
            ctx2.quadraticCurveTo(x + w, y, x + w, y + r);
            ctx2.lineTo(x + w, y + h - r);
            ctx2.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            ctx2.lineTo(x + r, y + h);
            ctx2.quadraticCurveTo(x, y + h, x, y + h - r);
            ctx2.lineTo(x, y + r);
            ctx2.quadraticCurveTo(x, y, x + r, y);
            ctx2.closePath();
          };
          roundRect(cx - bw / 2, cy - bh / 2, bw, bh, 3);
          ctx2.shadowColor = "transparent";
          ctx2.shadowBlur = 0;
          ctx2.strokeStyle = borderColor;
          ctx2.lineWidth = 1;
          ctx2.strokeRect(cx - bw / 2, cy - bh / 2, bw, bh);
          ctx2.fillStyle = "#ffffff";
          ctx2.textAlign = "center";
          ctx2.textBaseline = "middle";
          ctx2.font = "bold 10px Arial";
          ctx2.fillText(text, cx, cy);
          ctx2.restore();
        });

        // Line labels
        pendingLineLabels.forEach((lbl) => {
          const bx = lbl.x - lbl.width / 2;
          const by = lbl.y - lbl.height / 2;
          ctx2.save();
          ctx2.shadowColor = lbl.color + "cc";
          ctx2.shadowBlur = 8;
          ctx2.strokeStyle = lbl.color;
          ctx2.lineWidth = 1;
          ctx2.strokeRect(bx, by, lbl.width, lbl.height);
          ctx2.shadowColor = "transparent";
          ctx2.shadowBlur = 0;
          ctx2.fillStyle = lbl.color;
          ctx2.textAlign = "center";
          ctx2.textBaseline = "middle";
          ctx2.font = "bold 10px Arial";
          ctx2.fillText(lbl.text, lbl.x, lbl.y);
          ctx2.restore();
        });

        // Connector lines (line labels -> their actual point)
        pendingLineLabels.forEach((lbl) => {
          const op = chart.getDatasetMeta(lbl.datasetIndex).data[lbl.pointIndex];
          if (op) {
            ctx2.save();
            const lb = lbl.y + lbl.height / 2;
            if (Math.abs(op.y - lb) > 5) {
              ctx2.beginPath();
              ctx2.strokeStyle = lbl.color + "80";
              ctx2.lineWidth = 0.8;
              ctx2.moveTo(lbl.x, lb);
              ctx2.lineTo(op.x, op.y);
              ctx2.stroke();
            }
            ctx2.restore();
          }
        });
      },
    };

    // ✅ DRAW RAMADAN BOXES PLUGIN — draws Ramadan callouts LAST (after value
    // labels), transparent fill exactly as before, so it always sits legibly
    // on top without covering values that already moved out of its way.
    const drawRamadanBoxesPlugin = {
      id: "drawRamadanBoxes",
      afterDatasetsDraw(chart) {
        const ctx = chart.ctx;
        pendingRamadanBoxes.forEach(({ chosenX, chosenY, bTop, bBot, BOX_W, BR, ARROW_W, dataX, arrowTipY, safeBaseCX, label }) => {
          const baseBL = safeBaseCX - ARROW_W / 2;
          const baseBR = safeBaseCX + ARROW_W / 2;

          const drawShape = () => {
            ctx.beginPath();
            ctx.moveTo(chosenX + BR, bTop);
            ctx.lineTo(chosenX + BOX_W - BR, bTop);
            ctx.arcTo(chosenX + BOX_W, bTop, chosenX + BOX_W, bTop + BR, BR);
            ctx.lineTo(chosenX + BOX_W, bBot);
            ctx.lineTo(baseBR, bBot);
            ctx.lineTo(dataX, arrowTipY);
            ctx.lineTo(baseBL, bBot);
            ctx.lineTo(chosenX, bBot);
            ctx.lineTo(chosenX, bTop + BR);
            ctx.arcTo(chosenX, bTop, chosenX + BR, bTop, BR);
            ctx.closePath();
          };

          ctx.save();
          ctx.shadowColor = "rgba(255, 215, 0, 0.6)";
          ctx.shadowBlur  = 12;
          ctx.strokeStyle = "#ffdd57";
          ctx.lineWidth   = 0.5;
          drawShape();
          ctx.stroke();
          ctx.shadowColor = "transparent";
          ctx.shadowBlur  = 0;
          ctx.fillStyle   = "transparent";
          drawShape();
          ctx.fill();
          ctx.strokeStyle = "#ffdd57";
          ctx.lineWidth   = 1.4;
          drawShape();
          ctx.stroke();
          ctx.fillStyle    = "#ffffff";
          ctx.textAlign    = "center";
          ctx.textBaseline = "middle";
          ctx.font         = "bold 10px Arial";
          ctx.fillText(label, chosenX + BOX_W / 2, (bTop + bBot) / 2);
          ctx.restore();
        });
      },
    };

    // ── alignTablePlugin ── keeps the table's label column + total width lined
    // up with the chart's real plot-area pixels (measured from the actual
    // canvas), instead of relying on a fixed guessed width.
    const alignTablePlugin = {
      id: "alignTable",
      afterLayout(chart) {
        const area = chart.chartArea;
        if (!area || !containerRef.current || !tableRef.current) return;

        const canvasRect = containerRef.current.getBoundingClientRect();
        const tableRect = tableRef.current.getBoundingClientRect();

        const targetLeft = canvasRect.left + area.left;
        const targetRight = canvasRect.left + area.right;

        const labelWidth = Math.max(120, targetLeft - tableRect.left);
        const totalWidth = labelWidth + (targetRight - targetLeft);

        tableRef.current.style.width = `${totalWidth}px`;
        if (labelColRef.current) {
          labelColRef.current.style.width = `${labelWidth}px`;
        }
      },
    };

    chartInstanceRef.current = new Chart.Chart(ctx, {
      type: "bar",
      data: {
        labels: months,
        datasets: [
          {
            label: pndLabel,
            data: pndData.map(v => v == null ? 0 : v),
            type: "bar",
            backgroundColor: "transparent",
            borderColor: colors.pnd,
            borderWidth: 1.5,
            barPercentage: 0.6,
            categoryPercentage: 0.7,
            stack: "revenue",
            order: 2,
            yAxisID: "y",
          },
          {
            label: appWebLabel,
            data: appWebData.map(v => v == null ? 0 : v),
            type: "bar",
            backgroundColor: "transparent",
            borderColor: colors.appWeb,
            borderWidth: 1.5,
            barPercentage: 0.6,
            categoryPercentage: 0.7,
            stack: "revenue",
            order: 3,
            yAxisID: "y",
          },
          {
            label: ratioLabel,
            data: appToTotalData.map(v => v == null ? 0 : v),
            type: "line",
            borderColor: colors.ratio,
            backgroundColor: "transparent",
            borderWidth: 2,
            borderDash: [6, 3],
            // pointStyle: "circle",
            pointRadius: 0,
            pointBackgroundColor: colors.ratio,
            pointBorderColor: colors.ratio,
            tension: 0,
            order: 1,
            yAxisID: "y2",
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        animation: false,  
        devicePixelRatio: Math.max((window.devicePixelRatio || 1) * 2, 3),
        interaction: { mode: null },
        hover: { mode: null },
        events: [],
        plugins: {
          legend: {
            display: true,
            position: "top",
            labels: {
              color: "#ffffff",
              usePointStyle: false,
              boxWidth: 24,
              boxHeight: 12,
              generateLabels: (chart) =>
                chart.data.datasets.map((ds, i) => ({
                  text: ds.label,
                  datasetIndex: i,
                  hidden: false,
                  fillStyle: "transparent",
                  strokeStyle: "transparent",
                  lineWidth: 0,
                  fontColor: "#ffffff",
                })),
            },
          },
          tooltip: { enabled: false },
        },
        scales: {
          x: {
            stacked: true,
            offset: true,
            grid: { display: false },
            ticks: { display: false },
            border: { display: false },
          },
         y: {
  stacked: true,
  position: "left",
  grid: { display: false },

  min: yBounds.min,   // 👈 ADD
  max: yBounds.max,   // 👈 ADD

  ticks: {
    color: "#e5e7eb",
    font: { size: 9 },
    callback: (v) =>
      typeof v === "number" ? v.toLocaleString() : v,
  },
  border: { display: false },
  title: {
    display: true,
    text: yAxisLabel,
    color: "#e5e7eb",
    font: { size: 9 },
  },
},
          y2: {
            position: "right",
            grid: { display: false },
            ticks: {
              color: "#e5e7eb",
              font: { size: 9 },
              callback: (v) => (typeof v === "number" ? v.toFixed(0) + "%" : v),
            },
            border: { display: false },
            min: y2Bounds.min,
            max: y2Bounds.max,
          },
        },
        layout: {
          padding: { left: 40, right: 20, top: 0, bottom: 0 },
        },
      },
      plugins: [customLegendPlugin, shadowPlugin, lineShadowPlugin, barLabelPlugin, lineLabelPlugin, ramadanPlugin, drawValueLabelsPlugin, drawRamadanBoxesPlugin, alignTablePlugin],
    });

   let resizeTimeout = null;

const resizeObserver = new ResizeObserver(() => {
  if (!containerRef.current || !chartInstanceRef.current) return;

  clearTimeout(resizeTimeout);

  resizeTimeout = setTimeout(() => {
    const w = containerRef.current.offsetWidth;
    const h = containerRef.current.offsetHeight;
    chartInstanceRef.current.resize(w, h);
  }, 150);
});
if (containerRef.current) resizeObserver.observe(containerRef.current);

return () => {
  resizeObserver.disconnect();
  if (chartInstanceRef.current) {
    chartInstanceRef.current.destroy();
    chartInstanceRef.current = null;
  }
};
  }, [
    pndData, appWebData, appToTotalData, months, yearRanges,
    getRamadanMonths, showRamadanRow,
    appWebLabel, pndLabel, ratioLabel, colors, yAxisLabel,
    showDataLabels, y2Bounds,
  ]);

  // ── Table legend indicators ──
  const AppWebIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 14, height: 8, backgroundColor: "transparent", border: `1.5px solid ${colors.appWeb}`, borderRadius: 1 }} />
    </span>
  );

  const PndIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <span style={{ display: "inline-block", width: 14, height: 8, backgroundColor: "transparent", border: `1.5px solid ${colors.pnd}`, borderRadius: 1 }} />
    </span>
  );

  const RatioIndicator = () => (
    <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4, verticalAlign: "middle" }}>
      <svg width="20" height="10" viewBox="0 0 20 10">
        <line x1="0" y1="5" x2="20" y2="5" stroke={colors.ratio} strokeWidth="2" strokeDasharray="6,3" />
      </svg>
    </span>
  );

  return (
    <div className="w-full h-full flex items-center justify-center px-2 md:px-4">
      <div
        className="w-full mx-auto px-3 md:px-6 lg:px-8 py-4 md:py-8 rounded-lg shadow-lg bg-card"
        style={{ backgroundColor: "#ffffff" }}
      >
        {/* Header Section */}
        <div
          className="text-center"
          style={{
            backgroundColor: "#ffffff",
            padding: "0.1rem 2rem 0.25rem 2rem",
          }}
        >
          {title && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"main"}>{title}</HeadingComponent>
            </div>
          )}
          {subtitle && (
            <div style={{ margin: 0, lineHeight: 1.1 }}>
              <HeadingComponent variant={"sub"}>{subtitle}</HeadingComponent>
            </div>
          )}
        </div>

        {/* Chart + Table */}
        <div
          className="w-full p-3"
          style={{ backgroundColor: "#464647", borderRadius: "8px" }}
        >
          {chartTitle && (
            <div className="text-center mt-2">
              <h3
                className="text-xs font-semibold tracking-wider"
                style={{ color: "#ffffff" }}
              >
                {chartTitle}
              </h3>
            </div>
          )}

          <div
            className="ml-3 h-[350px] relative"
            ref={containerRef}
            style={{ marginBottom: "0px" }}
          >
            <canvas ref={chartRef}></canvas>
          </div>

          <div className="overflow-x-auto">
            <table ref={tableRef} className="!border-collapse text-xs table-fixed">
              <colgroup>
                <col ref={labelColRef} style={{ width: 80 }} />
                {months.map((_, idx) => (
                  <col key={idx} />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <th
                    className="!border-r py-0.5 px-1 text-center font-normal"
                    style={{ borderColor: "#ffffff" }}
                  ></th>
                  {months.map((month, idx) => (
                    <th
                      key={idx}
                      className="!border-r !border-t px-0.5 py-0.5 text-center font-normal text-[8px]"
                      style={{ color: "#ffffff", borderColor: "#ffffff" }}
                    >
                      {month}
                    </th>
                  ))}
                </tr>
                <tr>
                  <td
                    className="!border-r !border-b py-0.5 px-1 text-center text-[9px]"
                    style={{ borderColor: "#ffffff" }}
                  ></td>
                  {yearRanges.map((range, idx) => (
                    <td
                      key={idx}
                      colSpan={range.span}
                      className="!border !border-border px-0.5 py-0.5 text-center text-[9px]"
                      style={{ color: "#ffffff", borderColor: "#ffffff" }}
                    >
                      {range.year}
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    Icon: AppWebIndicator,
                    label: appWebLabel,
                    getData: (v) => v != null ? v.toLocaleString() : "0",
                    dataArr: appWebData,
                  },
                  {
                    Icon: PndIndicator,
                    label: pndLabel,
                    getData: (v) => v != null ? v.toLocaleString() : "0",
                    dataArr: pndData,
                  },
                  {
                    Icon: RatioIndicator,
                    label: ratioLabel,
                    getData: (v) => v != null ? v.toFixed(0) + "%" : "0",
                    dataArr: appToTotalData,
                  },
                ].map((row, ri) => (
                  <tr key={ri}>
                    <td
                      className="!border-l !border-r !border-b px-1 py-0.5 font-normal text-[7px] text-left whitespace-nowrap"
                      style={{ color: "#ffffff", borderColor: "#ffffff" }}
                    >
                      <row.Icon />
                      {row.label}
                    </td>
                    {row.dataArr.map((v, idx) => (
                      <td
                        key={idx}
                        className="!border-r !border-b px-0.5 py-[1px] text-center text-[8px]"
                        style={{ color: "#ffffff", borderColor: "#ffffff" }}
                      >
                        {row.getData(v)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div
              className="text-center py-1 text-[9px]"
              style={{ color: "#ffffff" }}
            >
              {bottomLabel}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewStackedChart;